/* eslint-disable no-param-reassign */
import {
  Box,
  Container,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useTranslation } from 'react-i18next';
import { BigLoader } from 'src/shared/components/organisms/bigLoader';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

import { Delete, Lock } from '@mui/icons-material';
import { getNewWebFormEvent } from 'src/shared/services/webFormEventService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import EventSignUpForm from '../components/eventSignUpForm';
import { Event } from '../models/event';

const EventSignUpPage = () => {
  // const [searchParams, setSearchParams] = useSearchParams();

  // // Get a specific query parameter
  // const myParam = searchParams.get('myParam');
  const { id, custname } = useParams();
  const isMounted = useIsMounted();
  const { t } = useTranslation(['event', 'common']);

  const [event, setEvent] = useState<Event>();
  const [loading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getNewWebFormEvent(parseInt(id ?? '', 10), custname ?? '');
      if (isMounted()) {
        setEvent(res.data);
        ConsoleHelper.log('RES:: ', res.data);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, [custname, id, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const TableWrapper = ({ children }) => {
    return (
      <TableContainer>
        <Table
          size='small'
          sx={{
            '& th': {
              whiteSpace: 'nowrap',
              pr: '1rem !important',
            },
            '& td': {
              width: '100%',
            },
            '& td, & th': { border: 0 },
          }}
        >
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  const tabelRow = (label: string, value: string | undefined, removeId?: string, locked?: boolean) => {
    if (!value) return null;
    return (
      value && (
        <TableRow key={`${label}-${value}-${removeId}`}>
          <TableCell component={'th' as any} scope='row' sx={{ verticalAlign: 'top' }}>
            <Typography variant='body1' sx={{ fontWeight: 600 }}>
              {label}:
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant='body1'>{value}</Typography>
          </TableCell>
          {removeId && (
            <TableCell padding='none' sx={{ p: '0 !important' }}>
              {locked && (
                <Tooltip arrow title={<>{t('member:memberDetails.lockedProfile')}</>} placement='top'>
                  <span>
                    <IconButton size='small' disabled>
                      <Lock />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!locked && (
                <Tooltip arrow title={<>{t('common:button.delete')}</>} placement='top'>
                  <span>
                    <IconButton size='small' onClick={() => ConsoleHelper.log(removeId)}>
                      <Delete fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </TableCell>
          )}
        </TableRow>
      )
    );
  };

  return (
    <Container maxWidth='sm'>
      <CssBaseline />
      <Paper sx={{ mt: 3 }}>
        <Box
          sx={{
            p: 2,
            position: 'relative',
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', p: 6, flexDirection: 'column' }}>
              <BigLoader message={t('common:statusMessages.loadingForm')} />
            </Box>
          )}
          {!loading && (
            <GridWrapper>
              <Grid item xs={12}>
                <Box py={2}>
                  <Typography variant='h4'>{event?.name ?? ''}</Typography>
                </Box>
                <TableWrapper>
                  {tabelRow(t('event:lastDate'), DateHelper.getDate(event?.lastDateForApplication))}
                  {tabelRow(t('event:dateForEvent'), DateHelper.getDate(event?.date))}
                </TableWrapper>
                <TableWrapper>
                  {tabelRow(t('event:address'), event?.address)}
                  {tabelRow(t('event:zipCode'), event?.postcode)}
                  {tabelRow(t('event:city'), event?.city)}
                </TableWrapper>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <EventSignUpForm eventId={parseInt(id ?? '', 10)} />
              </Grid>
            </GridWrapper>
          )}
        </Box>
      </Paper>
      <Box sx={{ mb: 3, mt: 1, textAlign: 'right' }}>
        <Typography variant='caption'>
          Powered by <Link href='https://foreningssupport.se'>Föreningssupport</Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default EventSignUpPage;
